import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createCompletion } from "../api/completionApi";
import Header from "../components/Header";
import Button from "../components/Button";
import Footer from "../components/Footer";
function SearchForm() {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showToast, setShowToast] = useState(false);
  const navigate = useNavigate();

  // Hide toast after 2.5 seconds
  React.useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!input.trim()) {
      setError("Please describe your legal issue.");
      setShowToast(true);
      return;
    }
    setLoading(true);
    try {
      const response = await createCompletion(input);
      // Redirect to SearchAI.js and pass response data
      navigate('/search-ai', { state: response });
    } catch (err) {
      setError("Failed to get results. Please try again.");
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form className="flex w-full rounded-md shadow-lg" aria-label="Search for a lawyer" onSubmit={handleSubmit}>
        <div className="flex flex-1 items-stretch w-full min-w-0">
          <textarea
            className="form-textarea flex-1 min-w-0 rounded-l-md border border-gray-300 bg-white text-base text-gray-800 placeholder-gray-400 focus:border-blue-600 focus:ring-blue-600 resize-none min-h-[72px] max-h-[240px]"
            placeholder="Describe your legal issue, e.g.: I got into a car accident and need help with insurance claims."
            rows={4}
            style={{ minHeight: '72px', maxHeight: '240px' }}
            aria-label="Describe your legal issue"
            aria-required="true"
            value={input}
            onChange={e => setInput(e.target.value)}
            disabled={loading}
          />
        </div>
        <Button type="submit" className="rounded-l-none min-w-[120px] flex items-center gap-2 justify-center" disabled={loading}>
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin material-symbols-outlined">autorenew</span>
              Loading...
            </span>
          ) : (
            <>
              <span className="material-symbols-outlined text-xl">search</span>
              Search
            </>
          )}
        </Button>
      </form>
      {loading && (
        <div className="flex justify-center items-center mt-8">
          <span className="animate-spin material-symbols-outlined text-4xl text-blue-600">autorenew</span>
          <span className="ml-3 text-lg text-blue-600">Searching for results...</span>
        </div>
      )}
      {showToast && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded shadow-lg z-50 text-sm font-semibold animate-fade-in-out">
          {error}
        </div>
      )}
    </>
  );
}

const Home = () => {
  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-white text-gray-800" style={{ fontFamily: 'Public Sans, Noto Sans, sans-serif' }}>
      <Header />
      <main className="flex flex-1 flex-col" role="main">
        <section className="relative flex min-h-[520px] items-center justify-center bg-gray-100 py-20" aria-label="Hero">
          <div className="relative z-10 mx-auto flex w-full max-w-4xl flex-col items-center gap-8 px-4 md:px-10 text-center">
            <div className="flex flex-col gap-4">
              <h1 className="text-5xl font-black leading-tight tracking-tighter text-gray-800">Find the right lawyer, right away.</h1>
              <p className="text-lg text-gray-600">Tell us about your case. We'll find the best legal expert for you.</p>
            </div>
            <div className="w-full max-w-2xl">
              <SearchForm />
            </div>
          </div>
        </section>
        <section className="mx-auto w-full max-w-6xl px-4 md:px-10 py-20" id="about" aria-label="About">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
            <div className="flex flex-col gap-6">
              <h2 className="text-3xl font-bold tracking-tight text-gray-800">About Our Platform</h2>
              <p className="text-lg text-gray-600">We connect you with experienced legal professionals who can provide the help you need. Our platform simplifies the process of finding a lawyer, making it accessible, transparent, and user-friendly. We believe everyone deserves access to quality legal representation.</p>
              <p className="text-lg text-gray-600">Our mission is to empower individuals by providing a reliable and efficient way to navigate the legal world. Whether you are dealing with a personal injury, a family matter, or a business dispute, we are here to help you find the right legal expert for your unique situation.</p>
            </div>
            <div>
              <img alt="Lawyers collaborating in a modern office, discussing legal documents around a table" className="rounded-lg shadow-lg" src="https://lh3.googleusercontent.com/aida-public/AB6AXuAW03v5QGOmD9R9szUbzRr6abyjp7lIj6rZKwZ-Qz3dQqskKRdtMB1k_6sfnDsypjimEbRTohp5IP_RBw7F0h3iOAN0Z7ouOa_0fHSKvOhNpjnze8vLvR_PJ3Zh6dwJ5J_7S6quwtEBSDlYJ-OeDzGxDlFc89sQF9pfONZH6OlKVVmMcPdI3cahcnVPqoxdT0J_-BzV2o0pKjEcKO86yA-U8Bzs0eCwgeXp0IrQ7iVlVod9YEclJA7QYhEqe5MnGj8gXJyBPfU8c5Ya" loading="lazy" />
            </div>
          </div>
        </section>
        <section className="py-20" id="contact" aria-label="Contact">
          <div className="mx-auto w-full max-w-6xl px-4 md:px-10">
            <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
              <div className="flex flex-col gap-6">
                <h2 className="text-3xl font-bold tracking-tight text-gray-800">Contact Us</h2>
                <p className="text-lg text-gray-600">Have questions? We're here to help. Reach out to us through our contact form or by phone. Our team is ready to assist you with any inquiries you may have.</p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="material-symbols-outlined text-[var(--primary-color)]">call</span>
                    <span className="text-lg text-gray-800">(123) 456-7890</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="material-symbols-outlined text-[var(--primary-color)]">mail</span>
                    <span className="text-lg text-gray-800">contact@needal.com</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="material-symbols-outlined text-[var(--primary-color)]">location_on</span>
                    <span className="text-lg text-gray-800">123 Legal Avenue, Suite 400, Lawtown, USA</span>
                  </div>
                </div>
              </div>
              <form className="space-y-6" aria-label="Contact form">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="name">Your Name</label>
                  <input className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600 bg-white text-gray-800 placeholder-gray-400" id="name" type="text" aria-required="true" aria-label="Your Name" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="contact-email">Your Email</label>
                  <input className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600 bg-white text-gray-800 placeholder-gray-400" id="contact-email" type="email" aria-required="true" aria-label="Your Email" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="message">Message</label>
                  <textarea className="form-textarea w-full rounded-md border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600 bg-white text-gray-800 placeholder-gray-400" id="message" rows="5" aria-required="true" aria-label="Message"></textarea>
                </div>
                <Button type="submit" className="w-full px-4 py-3 text-lg font-bold" aria-label="Send Message">Send Message</Button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Home;
